import React, { useCallback, useRef, useState } from 'react';
import styles from './styles.module.css';
import { convertToWebP, validateImageFile } from '../../../../../utils/imageConverter';

const MAX_SOURCE_SIZE_MB = 5;

const UploadIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const CloseIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/**
 * ImageUpload
 *
 * Props:
 *  - images: Array<{ file: File, dataUrl: string, originalSize: number, convertedSize: number }>
 *  - onChange: (nextImages) => void
 *  - maxFiles: number (optional, default unlimited)
 */
const ImageUpload = ({ images = [], onChange, maxFiles }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [converting, setConverting] = useState(false);
  const [errors, setErrors] = useState([]);
  const inputRef = useRef(null);

  const processFiles = useCallback(
    async (fileList) => {
      const incoming = Array.from(fileList);
      const validationErrors = [];
      const valid = [];

      for (const file of incoming) {
        const { valid: ok, error } = validateImageFile(file, MAX_SOURCE_SIZE_MB);
        if (!ok) {
          validationErrors.push(error);
        } else {
          valid.push(file);
        }
      }

      setErrors(validationErrors);
      if (valid.length === 0) return;

      setConverting(true);
      const results = await Promise.all(
        valid.map((f) =>
          convertToWebP(f, 35).catch((err) => {
            setErrors((prev) => [...prev, err.message]);
            return null;
          })
        )
      );
      setConverting(false);

      const converted = results.filter(Boolean);
      if (converted.length > 0) {
        const next = [...images, ...converted];
        onChange(maxFiles ? next.slice(0, maxFiles) : next);
      }
    },
    [images, onChange, maxFiles]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (!converting) processFiles(e.dataTransfer.files);
    },
    [converting, processFiles]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  }, []);

  const handleFileInput = (e) => {
    processFiles(e.target.files);
    e.target.value = '';
  };

  const handleRemove = (index) => {
    onChange(images.filter((_, i) => i !== index));
  };

  const openFilePicker = () => {
    if (!converting) inputRef.current?.click();
  };

  const isAtLimit = maxFiles != null && images.length >= maxFiles;

  return (
    <div className={styles.wrapper}>
      <label className={styles.sectionLabel}>Product Images</label>
      <p className={styles.sectionHint}>
        Images are automatically converted to WebP (&lt;35 KB). Max source size: {MAX_SOURCE_SIZE_MB} MB per file.
      </p>

      {!isAtLimit && (
        <div
          role="button"
          tabIndex={converting ? -1 : 0}
          aria-label="Upload product images"
          className={[
            styles.dropZone,
            isDragging ? styles.dragging : '',
            converting ? styles.busy : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={openFilePicker}
          onKeyDown={(e) => e.key === 'Enter' && openFilePicker()}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            className={styles.hiddenInput}
            onChange={handleFileInput}
            tabIndex={-1}
          />

          {converting ? (
            <div className={styles.busyState}>
              <span className={styles.spinner} />
              <span className={styles.busyText}>Converting to WebP…</span>
            </div>
          ) : (
            <div className={styles.dropContent}>
              <span className={styles.dropIcon}>
                <UploadIcon />
              </span>
              <p className={styles.dropPrimary}>
                Drag &amp; drop images here, or{' '}
                <span className={styles.browseLink}>browse files</span>
              </p>
              <p className={styles.dropSecondary}>
                JPG, PNG, GIF, WebP, BMP · up to {MAX_SOURCE_SIZE_MB} MB each
              </p>
            </div>
          )}
        </div>
      )}

      {errors.length > 0 && (
        <ul className={styles.errorList}>
          {errors.map((err, i) => (
            <li key={i} className={styles.errorItem}>
              {err}
            </li>
          ))}
        </ul>
      )}

      {images.length > 0 && (
        <div className={styles.previewGrid}>
          {images.map((img, idx) => (
            <div key={idx} className={styles.previewCard}>
              <img
                src={img.dataUrl}
                alt={`Product image ${idx + 1}`}
                className={styles.previewImg}
              />
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => handleRemove(idx)}
                aria-label={`Remove image ${idx + 1}`}
              >
                <CloseIcon />
              </button>
              <div className={styles.previewMeta}>
                <span className={styles.previewBadge}>
                  {img.existingId ? 'Saved' : 'WebP'}
                </span>
                {!img.existingId && img.convertedSize > 0 && (
                  <span className={styles.previewSize}>
                    {(img.convertedSize / 1024).toFixed(1)} KB
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
